/**
 * Satellites of a room being split: each goes with the piece it shares the
 * most wall with (spec §5.6) — carve the closet first, then the hallway, and
 * the closet stays with whichever room it actually opens off.
 *
 * "Shares a wall" is read the way the data records it. A satellite borders its
 * host ACROSS a wall: its outline runs on its own side's face and the host's on
 * the other, one wall thickness apart (0.08–0.21 m in the corpus), so no
 * polygon-distance tolerance tells a shared wall from a near one. What does is
 * a room wall on each side carrying the same `structureWallId` — the one
 * structure wall seen from both rooms — overlapping along it. 56 of the
 * corpus's 57 satellites share at least one such wall with their host.
 *
 * Tie → the kept room. A piece that is itself "Part of" the kept room cannot
 * host (no satellites of satellites), so its share goes to the kept room too.
 *
 * Pure. Changes a POINTER only.
 */

const { wallEndpoints } = require('./geometry');

/** Shared lengths closer than this are a tie (floating-point noise). */
const TIE_EPS_M = 1e-9;

/** Length over which two collinear-ish segments overlap, along `a`'s line. */
function overlapAlong(a, b) {
  const dx = a.br.x - a.bl.x;
  const dz = a.br.z - a.bl.z;
  const len = Math.hypot(dx, dz);
  if (!(len > 0)) return 0;
  const ux = dx / len;
  const uz = dz / len;
  const t = (p) => (p.x - a.bl.x) * ux + (p.z - a.bl.z) * uz;
  const b0 = t(b.bl);
  const b1 = t(b.br);
  const lo = Math.max(0, Math.min(b0, b1));
  const hi = Math.min(len, Math.max(b0, b1));
  return Math.max(0, hi - lo);
}

/** Total wall length `satellite` shares with `pieceWalls`. */
function sharedWallLength(satellite, pieceWalls) {
  const bySid = new Map();
  for (const w of pieceWalls || []) {
    const sid = w?.alignment?.structureWallId;
    const ends = sid && wallEndpoints(w);
    if (!ends) continue;
    if (!bySid.has(sid)) bySid.set(sid, []);
    bySid.get(sid).push(ends);
  }
  let total = 0;
  for (const w of satellite?.walls || []) {
    const sid = w?.alignment?.structureWallId;
    const mine = sid && bySid.get(sid);
    const ends = mine && wallEndpoints(w);
    if (!ends) continue;
    for (const other of mine) total += overlapAlong(ends, other);
  }
  return total;
}

/**
 * @param {object[]} rooms every room doc of the structure
 * @param {string} sourceRoomId the room being split
 * @param {{ kept: object[], created: object[] }} pieces each piece's room walls
 * @param {{ keptId: string, newId: string, newIsSatellite: boolean }} opts
 * @returns {object[]} copies of ONLY the satellites whose `parentRoomId`
 *   changes, re-pointed at the new room
 */
function rehostSatellites(rooms, sourceRoomId, pieces, opts) {
  const { newId, newIsSatellite } = opts || {};
  if (newIsSatellite || !newId) return [];
  const out = [];
  for (const room of rooms || []) {
    if (!room || room.parentRoomId !== sourceRoomId) continue;
    const toKept = sharedWallLength(room, pieces?.kept);
    const toNew = sharedWallLength(room, pieces?.created);
    if (toNew > toKept + TIE_EPS_M) out.push({ ...room, parentRoomId: newId });
  }
  return out;
}

module.exports = { rehostSatellites, sharedWallLength };
